import conversationCache from 'owa-mail-store/lib/store/conversationCache';
import ConversationSortOrder from 'owa-service/lib/contract/ConversationSortOrder';
import createEmptyConversationItemParts from '../utils/createEmptyConversationItemParts';
import deleteConversationItemParts from 'owa-mail-store/lib/actions/deleteConversationItemParts';
import GetConversationItemsResponseMessage from 'owa-service/lib/contract/GetConversationItemsResponseMessage';
import getConversationItemsService from 'owa-mail-store/lib/services/getConversationItemsService';
import getConversationSortOrderFromSettings from '../utils/getConversationSortOrderFromSettings';
import parseConversationResponse from './parseConversationResponse';
import { action } from 'satcheljs/lib/legacy';
import { emailContextValues } from 'mini-mail-bootstrap/lib/routing/mailViewHandler';
import { getStore } from 'owa-mail-store/lib/store/Store';
import { handleServerResponseSuccessAndError } from 'owa-service-utils/lib/handleServerResponseSuccessAndError';
import { MAIL_READING_PANE_LOAD_END } from 'mini-mail-workflow-tracking/lib/constants/ListViewWorkflowConstants';
import { signalWorkflowEvent } from 'mini-workflow-manager';
import { TraceErrorObject } from 'owa-trace';
import ConversationItemParts, {
    HasLoadState,
    PendingConversationRequestState,
} from 'owa-mail-store/lib/store/schema/ConversationItemParts';

export const INITIAL_MAX_ITEMS_TO_RETURN = 20;
export const LOAD_MORE_INCREMENT = 10;
export const MAX_ITEMS_ALLOWED_TO_RETRIEVE = 100;
export const MAX_RETRIES_ALLOWED = 2;

/**
 * Mark pendingRequestState as pending based on current syncState of conversation
 * This state causes GCI call once the current one finishes using the following codeflow:
 * a) Current syncState is empty: Pending GCI call is made with empty syncState in the future
 * b) Current syncState has value: Pending GCI call is made with latest syncState of the conversation
 * @param conversationToLoad is the conversation data from mail store
 */
function setPendingRequestState(conversationToLoad: ConversationItemParts) {
    switch (conversationToLoad.pendingRequestState) {
        case PendingConversationRequestState.NotPending:
        case PendingConversationRequestState.Pending:
            conversationToLoad.pendingRequestState = conversationToLoad.syncState
                ? PendingConversationRequestState.Pending
                : PendingConversationRequestState.PendingAndUseEmptySyncState;
            break;
        case PendingConversationRequestState.PendingAndUseEmptySyncState:
            // If one of pending requests needs to use empty syncState,
            // keep using empty sync state.
            // Therefore no op here.
            break;
    }
}

const processGetConversationItemsResponse = action('processGetConversationItemsResponse')(
    function processGetConversationItemsResponse(
        conversationSortOrder: ConversationSortOrder,
        conversationId: string,
        responseMessage: GetConversationItemsResponseMessage,
        maxRetries: number
    ) {
        parseConversationResponse(
            responseMessage,
            conversationSortOrder,
            conversationId,
            maxRetries
        );
        emailContextValues.idFound = responseMessage?.ResponseClass == 'Success' ? true : false;
        const loadedConversation = conversationCache.get(conversationId);
        loadedConversation.loadingState.isLoading = false;

        // A pendingRequestState mandates that we make a new GCI call to get latest conversation data
        // which was deferred to avoid parallel GCI calls
        if (loadedConversation.pendingRequestState !== PendingConversationRequestState.NotPending) {
            loadConversation(conversationId);
        }
    }
);

const loadConversation = action('loadConversation')(function (
    conversationIdToLoad: string,
    maxRetries: number = MAX_RETRIES_ALLOWED
): Promise<void> {
    const conversationSortOrder = getConversationSortOrderFromSettings();
    let conversationToLoad = getStore().conversations.get(conversationIdToLoad);

    // If cached conversationSortOrder is different from userOption, delete the cache
    if (conversationToLoad && conversationToLoad.conversationSortOrder !== conversationSortOrder) {
        deleteConversationItemParts(conversationIdToLoad);
        conversationToLoad = null;
    }

    // If conversation has no cache or previous cache failed, create an empty one
    if (
        !conversationToLoad ||
        conversationToLoad.loadingState.hasLoadState === HasLoadState.hasFailed
    ) {
        conversationToLoad = createEmptyConversationItemParts(
            conversationIdToLoad,
            conversationSortOrder
        );
    }

    // Save empty conversationItemPart, or touch conversationCache to reduce the possibility that the conversation got purged from cache
    conversationCache.add(conversationIdToLoad, conversationToLoad);

    // For each new coming GCI request, we "queue" it as a pending request to guarantee only one ongoing GCI per conversation.
    // But we don't really queue requests, instead, we just set/update pending request status based on new coming GCI requests.
    setPendingRequestState(conversationToLoad);

    if (conversationToLoad.loadingState.isLoading) {
        // Ignore all GCI calls until the current one is done, we will fire GCI call in the future. pendingRequestState is set for this purpose.
        return Promise.resolve();
    } else {
        const shouldUseEmptySyncState =
            conversationToLoad.pendingRequestState ===
            PendingConversationRequestState.PendingAndUseEmptySyncState;
        conversationToLoad.loadingState.isLoading = true;

        const syncState = shouldUseEmptySyncState ? '' : conversationToLoad.syncState;

        // Clear pending GCI state since we are firing a GCI call for the pending request
        conversationToLoad.pendingRequestState = PendingConversationRequestState.NotPending;

        return getConversationItemsService(
            conversationToLoad.conversationId,
            syncState,
            conversationToLoad.maxItemsToReturn,
            conversationSortOrder
        )
            .then(handleServerResponseSuccessAndError)
            .then(responseMessage => {
                processGetConversationItemsResponse(
                    conversationSortOrder,
                    conversationIdToLoad,
                    responseMessage as GetConversationItemsResponseMessage,
                    maxRetries
                );
            })
            .catch((error: Error) => {
                // If owa-service throws an error, we want to catch it here; process as a null responseMessage
                // the error thrown already has the correct classification of error as FetchErrorType. so use it as is.
                processGetConversationItemsResponse(
                    conversationSortOrder,
                    conversationIdToLoad,
                    null /* responseMessage */,
                    maxRetries
                );
                if (error) {
                    error.message = `[GCI] ${error.message}`;

                    // If the traceErrorObject returned from fetchHandler (refer owa-service -> fetchHandler) has the error.response value,
                    // the error message sometimes gets set to undefined due to internal handling of owa-analytics.
                    // For the time being we are setting the value to be null so that we don't get empty  error messages in the data-point.
                    // The error message set in the above line is already resourceful enough to convey what went wrong
                    // Remove once owa-analytics fixes this.
                    // (https://outlookweb.visualstudio.com/Outlook%20Web/_git/client-web?path=%2Fshared%2Finternal%2Fowa-analytics%2Fsrc%2Futils%2FaddErrorToDatapoint.ts&version=GBmaster&line=22&lineEnd=23&lineStartColumn=1&lineEndColumn=1&lineStyle=plain&_a=contents)
                    (error as TraceErrorObject).response = null;
                }
                signalWorkflowEvent(
                    MAIL_READING_PANE_LOAD_END,
                    undefined,
                    { isCore: true },
                    { error }
                );
            });
    }
});

export default loadConversation;
